import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Upload, Download, RefreshCw, Archive, History, Loader2, ShieldCheck, CheckSquare } from 'lucide-react';

const DOC_TYPE_LABELS = {
  identification: ['Identificación', 'Identification'], contract: ['Contrato', 'Contract'],
  certification: ['Certificación', 'Certification'], license: ['Licencia', 'License'],
  regulatory: ['Regulatorio', 'Regulatory'], work_permit: ['Permiso de trabajo', 'Work permit'],
  training_certificate: ['Constancia de capacitación', 'Training certificate'],
  cv: ['CV / Hoja de vida', 'CV / Résumé'], application: ['Solicitud', 'Application'],
  diploma: ['Título académico', 'Academic degree'],
  credential: ['Credencial', 'Credential'], onboarding_form: ['Formato de ingreso', 'Onboarding form'],
  acknowledgment: ['Acuse', 'Acknowledgment'], evidence: ['Evidencia', 'Evidence'],
  handover: ['Entrega de puesto', 'Handover'], exit_form: ['Formato de salida', 'Exit form'],
  continuity: ['Continuidad', 'Continuity'], knowledge_transfer: ['Transferencia de conocimiento', 'Knowledge transfer'],
  other: ['Otro', 'Other'],
};

// V5.0 §11 — reusable secure document area (Documentos / Evidencias).
// V5.1 §B.2 — supports deferred entity creation (resolveEntityId) + readOnly.
export const DocumentPanel = ({ suite, entityType, entityId, employeeUserId = null,
                                defaultDocType = 'other', docTypes = null,
                                mode = 'client', companyId = null, compact = false,
                                resolveEntityId = null, readOnly = false }) => {
  const { i18n } = useTranslation();
  const isES = (i18n.language || 'es').startsWith('es');
  const L = (es, en) => (isES ? es : en);
  const [eid, setEid] = useState(entityId || null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState(defaultDocType);
  const [auditDoc, setAuditDoc] = useState(null);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);
  const [uploadExp, setUploadExp] = useState('');
  const canWrite = mode === 'client' && !readOnly;
  useEffect(() => { setEid(entityId || null); }, [entityId]);

  const load = useCallback(async (idArg) => {
    const id = idArg || eid;
    if (!id) { setItems([]); return; }
    try {
      const url = mode === 'client'
        ? `/client/documents?suite_code=${suite}&entity_id=${encodeURIComponent(id)}`
        : `/admin/documents?company_id=${companyId}&suite_code=${suite}&entity_id=${encodeURIComponent(id)}`;
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [suite, eid, mode, companyId]);
  useEffect(() => { load(); }, [eid, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      let id = eid;
      if (!id) {
        if (!resolveEntityId) { setUploading(false); return; }
        id = await resolveEntityId();
        if (!id) { setUploading(false); return; }
        setEid(id);
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('suite_code', suite);
      fd.append('entity_type', entityType);
      fd.append('entity_id', id);
      fd.append('doc_type', docType);
      if (uploadExp) fd.append('expiration_date', uploadExp);
      if (employeeUserId) fd.append('employee_user_id', employeeUserId);
      await api.post('/client/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
      toast.success(L('Documento cargado', 'Document uploaded'));
      load(id);
    } catch (e) { toast.error(formatApiError(e)); } finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const r = await api.get(`/documents/${d.id}/download`, { responseType: 'blob', timeout: 90000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = d.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const replace = async (file) => {
    if (!file || !replacingId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/documents/${replacingId}/replace`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 90000 });
      toast.success(L('Documento reemplazado (nueva versión)', 'Document replaced (new version)'));
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); setReplacingId(null); }
  };

  const archive = async (d) => {
    try { await api.post(`/documents/${d.id}/archive`); toast.success(L('Documento archivado', 'Document archived')); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // V5.3 §A — per-document expiration date (optional, audited)
  const setExpiration = async (d, value) => {
    try {
      await api.patch(`/documents/${d.id}/expiration`, { expiration_date: value || null });
      toast.success(L('Vencimiento actualizado', 'Expiration updated'));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const expState = (exp) => {
    if (!exp) return null;
    const today = new Date().toISOString().slice(0, 10);
    const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
    if (exp <= today) return 'expired';
    if (exp <= soon) return 'soon';
    return 'ok';
  };

  const typeOptions = docTypes || Object.keys(DOC_TYPE_LABELS);
  const active = items.filter((d) => d.status === 'active');
  const archived = items.filter((d) => d.status === 'archived');

  return (
    <div className={`rounded-lg border ${compact ? 'p-3' : 'p-4'} space-y-3`} data-testid={`doc-panel-${suite}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldCheck className="h-4 w-4 text-teal-700" />
        <p className="text-sm font-semibold">{L('Documentos / Evidencias', 'Documents / Evidence')}</p>
        <p className="text-xs text-muted-foreground -mt-0.5" data-testid="doc-panel-storage-note">
          {L('Archivos almacenados de forma segura dentro de CTH (cifrado en tránsito, acceso autenticado y auditado).',
             'Files stored securely within CTH (encrypted in transit, authenticated and audited access).')}
        </p>
        <Badge variant="outline" className="text-[10px]">{active.length}</Badge>
        <span className="flex-1" />
        {canWrite && (
          <>
            <select value={docType} onChange={(e) => setDocType(e.target.value)}
                    className="h-8 rounded-md border bg-background px-2 text-xs"
                    data-testid="doc-panel-type-select">
              {typeOptions.map((k) => (
                <option key={k} value={k}>{(DOC_TYPE_LABELS[k] || [k, k])[isES ? 0 : 1]}</option>
              ))}
            </select>
            <input type="date" value={uploadExp} onChange={(e) => setUploadExp(e.target.value)}
                   title={L('Vencimiento del documento (opcional)', 'Document expiration (optional)')}
                   className="h-8 rounded-md border bg-background px-2 text-xs"
                   data-testid="doc-panel-expiration-input" />
            <Button size="sm" variant="outline" disabled={uploading}
                    onClick={() => fileRef.current?.click()} data-testid="doc-panel-upload-btn">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
              {L('Subir', 'Upload')}
            </Button>
            <input ref={fileRef} type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                   onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
                   data-testid="doc-panel-file-input" />
            <input ref={replaceRef} type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                   onChange={(e) => { replace(e.target.files?.[0]); e.target.value = ''; }} />
          </>
        )}
      </div>
      {active.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="doc-panel-empty">
          {L('Sin documentos. Formatos: PDF, DOC(X), XLS(X), JPG, PNG · máx. 10 MB.',
             'No documents. Formats: PDF, DOC(X), XLS(X), JPG, PNG · max 10 MB.')}
        </p>
      ) : (
        <div className="space-y-2">
          {active.map((d) => (
            <div key={d.id} className="flex items-center gap-2 rounded-md border bg-muted/20 px-2.5 py-2 text-xs flex-wrap"
                 data-testid={`doc-item-${d.id}`}>
              <FileText className="h-4 w-4 text-slate-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{d.filename}</p>
                <p className="text-muted-foreground">
                  {(DOC_TYPE_LABELS[d.doc_type] || [d.doc_type, d.doc_type])[isES ? 0 : 1]}
                  {' · v'}{d.version}{' · '}{(d.uploaded_at || '').slice(0, 10)}{' · '}{d.uploaded_by_name || '—'}
                  {d.expiration_date && (
                    <span className={expState(d.expiration_date) === 'expired' ? 'text-red-600 font-medium'
                      : expState(d.expiration_date) === 'soon' ? 'text-orange-600 font-medium' : ''}
                          data-testid={`doc-expiration-${d.id}`}>
                      {' · '}{expState(d.expiration_date) === 'expired' ? L('venció', 'expired') : L('vence', 'expires')} {d.expiration_date}
                    </span>
                  )}
                </p>
              </div>
              {canWrite && (
                <input type="date" defaultValue={d.expiration_date || ''}
                       onBlur={(e) => { if ((e.target.value || '') !== (d.expiration_date || '')) setExpiration(d, e.target.value); }}
                       title={L('Fecha de vencimiento del documento', 'Document expiration date')}
                       className="h-7 rounded-md border bg-background px-1.5 text-[11px] w-[8.2rem]"
                       data-testid={`doc-exp-edit-${d.id}`} />
              )}
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => download(d)}
                      data-testid={`doc-download-${d.id}`}>
                <Download className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setAuditDoc(d)}
                      data-testid={`doc-audit-${d.id}`}>
                <History className="h-3.5 w-3.5" />
              </Button>
              {canWrite && employeeUserId && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-indigo-600"
                        title={L('Solicitar acuse de recepción al colaborador', 'Request employee acknowledgment')}
                        onClick={async () => {
                          try {
                            await api.post(`/client/documents/${d.id}/ack-request`, { employee_user_id: employeeUserId });
                            toast.success(L('Acuse solicitado', 'Acknowledgment requested'));
                          } catch (e) {
                            const det = e?.response?.data?.detail;
                            toast.error(det === 'already_pending'
                              ? L('Ya existe un acuse pendiente para este documento', 'An acknowledgment is already pending for this document')
                              : formatApiError(e));
                          }
                        }}
                        data-testid={`doc-ack-request-${d.id}`}>
                  <CheckSquare className="h-3.5 w-3.5" />
                </Button>
              )}
              {canWrite && (
                <>
                  <Button size="sm" variant="ghost" className="h-7 px-2" disabled={busy}
                          onClick={() => { setReplacingId(d.id); replaceRef.current?.click(); }}
                          data-testid={`doc-replace-${d.id}`}>
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => archive(d)}
                          data-testid={`doc-archive-${d.id}`}>
                    <Archive className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {archived.length > 0 && (
        <p className="text-[11px] text-muted-foreground">{archived.length} {L('archivado(s)', 'archived')}</p>
      )}

      <Dialog open={!!auditDoc} onOpenChange={(o) => !o && setAuditDoc(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="doc-audit-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{L('Historial del documento', 'Document history')}</DialogTitle>
            <DialogDescription className="sr-only">
              {L('Acciones auditadas del documento', 'Audited document actions')}
            </DialogDescription>
          </DialogHeader>
          {auditDoc && (
            <div className="space-y-1.5 max-h-80 overflow-y-auto text-xs">
              {[...(auditDoc.audit || [])].reverse().map((a, i) => (
                <div key={i} className="rounded border px-2.5 py-1.5 bg-muted/20">
                  <span className="font-medium">{a.action}</span>
                  {' · '}{a.user_name || a.user_id}{' · '}{(a.at || '').slice(0, 16).replace('T', ' ')}
                  {a.from_version != null && <> · v{a.from_version} → v{a.to_version}</>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
